/**
 * Forwarded Message Card
 * Displays the original message context when a message has been forwarded
 */

import type { ForwardedFromMessage, MessageAttachment, MessageLink } from '@/types/api.types';
import Avatar from '@/components/common/Avatar';
import AttachmentDisplay from '@/components/attachments/AttachmentDisplay';
import LinkPreview from '@/components/messages/LinkPreview';
import { linkifyText } from '@/utils/linkify.util';

interface ForwardedMessageCardProps {
  originalMessage: ForwardedFromMessage | null | undefined;
  sourceChannel?: { id: string; name: string; type: string } | null;
  sourceDmThread?: { id: string; is_group: boolean } | null;
}

const ForwardedMessageCard = ({
  originalMessage,
  sourceChannel,
  sourceDmThread,
}: ForwardedMessageCardProps) => {
  // Source message was deleted
  if (!originalMessage) {
    return (
      <div className="mb-2 px-3 py-2 border-l-2 border-gray-300 dark:border-dark-border-secondary bg-gray-50 dark:bg-dark-bg-secondary/50 rounded-r-lg">
        <div className="flex items-center space-x-1.5 text-xs text-gray-400 dark:text-dark-text-muted">
          <ForwardIcon />
          <span className="italic">Оригиналното съобщение е изтрито</span>
        </div>
      </div>
    );
  }

  const sourceName = sourceChannel
    ? `#${sourceChannel.name}`
    : sourceDmThread
      ? sourceDmThread.is_group
        ? 'Групов чат'
        : 'Лично съобщение'
      : '';

  const originalAuthor = originalMessage.users;

  return (
    <div className="mb-2 px-3 py-2 border-l-2 border-blue-300 dark:border-blue-500/40 bg-blue-50/50 dark:bg-blue-900/10 rounded-r-lg">
      {/* Forward header */}
      <div className="flex items-center space-x-1.5 text-xs text-gray-500 dark:text-dark-text-muted mb-1.5">
        <ForwardIcon />
        <span>
          Препратено от{' '}
          <span className="font-medium text-gray-700 dark:text-dark-text-secondary">
            {originalAuthor.display_name}
          </span>
          {sourceName && (
            <>
              {' '}в{' '}
              <span className="font-medium text-gray-700 dark:text-dark-text-secondary">
                {sourceName}
              </span>
            </>
          )}
        </span>
      </div>

      {/* Original message content */}
      <div className="flex items-start space-x-2">
        <Avatar
          user={{
            id: originalAuthor.id,
            display_name: originalAuthor.display_name,
            avatar_url: originalAuthor.avatar_url,
          }}
          size="xs"
        />
        <div className="flex-1 min-w-0">
          {originalMessage.content && (
            <p className="text-sm text-gray-800 dark:text-dark-text-primary whitespace-pre-wrap break-words">
              {linkifyText(originalMessage.content)}
            </p>
          )}

          {/* Original attachments */}
          {originalMessage.message_attachments && originalMessage.message_attachments.length > 0 && (
            <AttachmentDisplay attachments={originalMessage.message_attachments as MessageAttachment[]} />
          )}

          {/* Original link previews */}
          {originalMessage.message_links && originalMessage.message_links.length > 0 && (
            <div className="mt-1 space-y-1">
              {(originalMessage.message_links as MessageLink[])
                .filter((link) => !link.hidden)
                .map((link) => (
                  <LinkPreview key={link.id} link={link} isAuthor={false} onHide={() => {}} />
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ForwardIcon = () => (
  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 7l5 5m0 0l-5 5m5-5H6"
    />
  </svg>
);

export default ForwardedMessageCard;
